import { apis } from "apis";
import ServiceBase from "./service-base";
import IHttpResponse from "src/http/http-response";
import HttpClient from "src/http/http-client";
import { ILoginModel, IRegisterModel, IUserModel } from "src/model";
import { ServiceInjectable } from "src/utils/services";

/**
 * 用户类
 * @class
 * @version 1.0.0
 */
@ServiceInjectable()
export default class UserService extends ServiceBase
{
    /**
     * 滑动验证码验证
     * @public
     * @returns {Promise<IHttpResponse>}
     */
    public async verify(data: any): Promise<IHttpResponse>
    {
        let response = await this.apis.verify({ data });
        return response;
    }

    /**
     * 登入
     * @public
     * @returns {Promise<IHttpResponse>}
     */
    public async login(loginData: ILoginModel): Promise<IHttpResponse>
    {
        let response = await this.apis.login({
            data: loginData
        });

        return response;
    }

    /**
     * 退出登录
     * @public
     * @returns {Promise<IHttpResponse>}
     */
    public async logout(): Promise<IHttpResponse>
    {
        return await this.apis.logout();
    }

    /**
     * 获取用户信息
     * @public
     * @returns {Promise<IHttpResponse>}
     */
    public async getLoginInfo(): Promise<IHttpResponse>
    {
        let response = await this.apis.getLoginInfo({});
        return response;
    }

    /**
     * 新增用户
     * @public
     * @returns {Promise<IHttpResponse>}
     */
    public async addUser(data: IUserModel): Promise<IHttpResponse>
    {
        let response = await this.apis.addUser({ data });
        return response;
    }

    /**
     * 删除用户
     * @public
     * @returns {Promise<IHttpResponse>}
     */
    public async deleteUser(data: any): Promise<IHttpResponse>
    {
        let response = await this.apis.deleteUser({ data });
        return response;
    }

    /**
     * 修改用户
     * @public
     * @returns {Promise<IHttpResponse>}
     */
    public async editUser(data: any): Promise<IHttpResponse>
    {
        let response = await this.apis.editUser({ data });
        return response;
    }

    /**
     * 修改用户状态
     * @public
     * @returns {Promise<IHttpResponse>}
     */
    public async changeUserStatus(id: number, status: number): Promise<IHttpResponse>
    {
        let response = await this.apis.changeUserStatus({
            params: {
                id: id,
                status: status
            }
        });
        return response;
    }

    /**
     * 重置用户密码
     * @public
     * @returns {Promise<IHttpResponse>}
     */
    public async resetUserPwd(id: number): Promise<IHttpResponse>
    {
        let response = await this.apis.resetUserPwd({
            params: { id: id }
        });
        return response;
    }

    /**
     * 获取用户分页数据
     * @param id 用户id
     * @public
     * @returns {Promise<IHttpResponse>}
     */
    public async getSKCUserDetail(key: string): Promise<IHttpResponse>
    {
        let response = await this.apis.getSKCUserDetail({ 
            params: { key }
        });
        return response;
    }

    /**
     * 获取用户分页数据
     * @param id 用户id
     * @public
     * @returns {Promise<IHttpResponse>}
     */
    public async getUserPage(data: any): Promise<IHttpResponse>
    {
        let response = await this.apis.getUserPage({ 
            data: data
        });
        return response;
    }

    /**
     * 获取用户详情
     * @param id 用户id
     * @public
     * @returns {Promise<IHttpResponse>}
     */
    public async getUserDetail(id: number): Promise<IHttpResponse>
    {
        let response = await this.apis.getUserDetail({ 
            urlPath: { id: id } 
        });
        return response;
    }

    /**
     * 获取用户拥有数据
     * @param id 用户id
     * @public
     * @returns {Promise<IHttpResponse>}
     */
    public async getUserOwnData(id: number): Promise<IHttpResponse>
    {
        let response = await this.apis.getUserOwnData({ 
            params: { id: id } 
        });
        return response;
    }

    /**
     * 设置用户数据权限
     * @public
     * @returns {Promise<IHttpResponse>}
     */
    public async grantUserData(data: any): Promise<IHttpResponse>
    {
        let response = await this.apis.grantUserData({ 
        data: data
        });
        return response;
    }

    /**
     * 获取用户拥有数据
     * @param id 用户id
     * @public
     * @returns {Promise<IHttpResponse>}
     */
    public async getUserOwnRole(id: number): Promise<IHttpResponse>
    {
        let response = await this.apis.getUserOwnRole({ 
            params: { id: id } 
        });
        return response;
    }
    
    /**
     * 设置用户角色
     * @public
     * @returns {Promise<IHttpResponse>}
     */
    public async grantUserRole(data: any): Promise<IHttpResponse>
    {
        let response = await this.apis.grantUserRole({ 
            data: data
        });
        return response;
    }

    /**
     * 验证用户名手机号是否重复
     * 验证类型（0：用户名; 1: 手机号码)
     * @public
     * @returns {Promise<IHttpResponse>}
     */
    public async skcuserValidate(data: any): Promise<IHttpResponse>
    {
        let response = await this.apis.skcuserValidate({ 
            data: data
        });
        return response;
    }

    /**
     * 用户注册
     * @public
     * @returns {Promise<IHttpResponse>}
     */
    public async skcRegister(data: any): Promise<IHttpResponse>
    {
        let response = await this.apis.skcRegister({ 
            data: data
        });

        return response;
    }

    /**
     * 新增租户(注册)
     * @public
     * @returns {Promise<IHttpResponse>}
     */
     public async addTenant(data: any): Promise<IHttpResponse>
     {
         let response = await this.apis.addTenant({ 
            data
         });
         return response;
    }

    /**
     * 获取档期用户相关店铺
     * @public
     * @returns {Promise<IHttpResponse>}
     */
     public async getMyTenants(): Promise<IHttpResponse>
     {
        let response = await this.apis.getMyTenants({ });
        return response;
    }

    /**
     * 修改密码
     * @public
     * @returns {Promise<IHttpResponse>}
     */
    public async changePassword(data: any): Promise<IHttpResponse>
    {
        let response = await this.apis.changePassword({ 
            data: data
        });

        return response;
    }

    /**
     * 用户修改信息
     * @public
     * @returns {Promise<IHttpResponse>}
     */
     public async skcuser(data: any): Promise<IHttpResponse>
     {
         let response = await this.apis.skcuser({ 
             data: data
         });
 
         return response;
     }
    
}